import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiUsers, FiTrendingUp, FiAlertCircle, FiBriefcase, 
  FiPlus, FiSearch, FiFilter, FiArrowRight, FiActivity,
  FiFileText, FiCalendar, FiMoreVertical
} = FiIcons;
import { CLIENT_PORTFOLIO } from '../data/consultantMocks';
import { motion } from 'framer-motion';

export const ConsultantToolkit = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusColor = (status) => {
    switch(status) {
      case 'Critical': return 'rose';
      case 'Active': return 'indigo';
      case 'Stable': return 'emerald';
      default: return 'slate';
    }
  };

  const filteredClients = CLIENT_PORTFOLIO.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 pb-20">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-indigo-500" />
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Consultant Command Center</span>
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Klientportefølje</h1>
          <p className="text-slate-500 font-medium">Administrer målinger, prosjekter og tiltak for dine tildelte virksomheter</p>
        </div>

        <div className="flex gap-3">
          <div className="bg-white px-4 py-2 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <SafeIcon icon={FiBriefcase} size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Aktive Klienter</p>
              <p className="text-sm font-black text-slate-900">12 Virksomheter</p>
            </div>
          </div>
          <button className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest transition-all shadow-lg flex items-center gap-2">
            <SafeIcon icon={FiPlus} /> Ny Klient
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Søk i klienter, prosjekter eller industrier..."
            className="w-full pl-12 pr-4 py-4 bg-white rounded-2xl border-2 border-transparent focus:border-indigo-500 shadow-sm transition-all text-sm font-medium"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="bg-white border-2 border-slate-100 px-6 py-4 rounded-2xl flex items-center gap-2 text-slate-600 font-bold text-sm hover:border-indigo-200 transition-all">
          <SafeIcon icon={FiFilter} /> Filtre
        </button>
      </div>

      {/* Portfolio Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {filteredClients.map((client, idx) => {
          const color = getStatusColor(client.status);
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              key={client.id}
            >
              <Card className="border-none shadow-xl hover:shadow-2xl transition-all group overflow-hidden bg-white">
                <CardContent className="p-0">
                  <div className="p-6 flex items-start justify-between">
                    <div className="flex gap-4">
                      <div className={`w-14 h-14 rounded-2xl bg-${color}-50 flex flex-col items-center justify-center border border-${color}-100`}>
                        <span className={`text-xl font-black text-${color}-600`}>{client.score}</span>
                        <span className={`text-[8px] font-black text-${color}-400 uppercase tracking-tighter`}>Score</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-black text-slate-900">{client.name}</h3>
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-${color}-100 text-${color}-700`}>
                            {client.status}
                          </span>
                        </div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-0.5">{client.industry}</p>
                      </div>
                    </div>
                    <button className="text-slate-300 hover:text-slate-600 p-2">
                      <SafeIcon icon={FiMoreVertical} />
                    </button>
                  </div>

                  {/* Client Metrics */}
                  <div className="px-6 py-4 bg-slate-50/50 grid grid-cols-3 gap-4 border-y border-slate-100">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter mb-1">Risikonivå</p>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full bg-${client.riskLevel === 'High' ? 'rose' : 'amber'}-500`} />
                        <span className="text-xs font-bold text-slate-700">{client.riskLevel}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter mb-1">Trend</p>
                      <div className="flex items-center gap-1 text-emerald-600">
                        <SafeIcon icon={FiTrendingUp} size={12} />
                        <span className="text-xs font-bold">{client.trend}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter mb-1">Siste Måling</p>
                      <span className="text-xs font-bold text-slate-700">{client.lastAssessment}</span>
                    </div>
                  </div>

                  {/* Active Project Info */}
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                          <SafeIcon icon={FiActivity} size={16} />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Pågående Prosjekt</p>
                          <p className="text-sm font-bold text-slate-800">{client.activeProject}</p>
                        </div>
                      </div>
                      <div className="flex -space-x-2">
                         {[1,2,3].map(i => (
                           <div key={i} className="w-7 h-7 rounded-full border-2 border-white bg-slate-200" />
                         ))}
                      </div>
                    </div>

                    <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100 flex items-center justify-between group-hover:bg-indigo-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiCalendar} className="text-indigo-500" />
                        <div>
                          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-tighter">Neste Milepæl</p>
                          <p className="text-xs font-bold text-indigo-900">{client.nextAction}</p>
                        </div>
                      </div>
                      <button className="bg-white p-2 rounded-lg text-indigo-600 shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-all">
                        <SafeIcon icon={FiArrowRight} />
                      </button>
                    </div>
                  </div>

                  {/* Action Bar */}
                  <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-white">
                    <div className="flex gap-4">
                      <button className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase hover:text-indigo-600 transition-colors">
                        <SafeIcon icon={FiFileText} /> Rapporter
                      </button>
                      <button className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase hover:text-indigo-600 transition-colors">
                        <SafeIcon icon={FiUsers} /> Team
                      </button>
                    </div>
                    <span className="text-[10px] font-black text-slate-300 uppercase italic">Ref: CX-{client.id.toUpperCase()}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ConsultantToolkit;